import { API_BASE_URL } from '@types';

export type UserCustomAudioVideo = {
  id: number;
  name: string;
  category: string | null;
  product_id: number | null;
  product_name: string | null;
  suite_type: string | null;
  audio_row_id: number;
};

export type UserCustomAudioGroup = {
  id: number;
  name: string;
  url: string;
  md5?: string | null;
  lang: string;
  user_id: number;
  size?: number | null;
  type?: string | null;
  create_time?: string | Date | null;
  update_time?: string | Date | null;
  videos: UserCustomAudioVideo[];
};

export type LinkableVideo = {
  id: number;
  name: string;
  category: string | null;
  product_id: number;
  product_name: string;
  suite_type: string | null;
};

export type PresignUserCustomAudioResponse = {
  putUrl: string;
  publicUrl: string;
  key: string;
  bucket: string;
  contentType: string;
};

export type CreateUserCustomAudiosPayload = {
  name: string;
  lang: string;
  url: string;
  videoIds?: number[];
  type?: string | null;
  size?: number | null;
  md5?: string | null;
};

export type UploadProgress = {
  loaded: number;
  total: number;
  percent: number;
};

async function parseJson<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message =
      (body as { error?: string; message?: string })?.error ||
      (body as { message?: string })?.message ||
      `HTTP ${response.status}: ${response.statusText}`;
    throw new Error(message);
  }
  return body as T;
}

export async function fetchUserCustomAudios(
  userId: string | number
): Promise<UserCustomAudioGroup[]> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios`,
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const result = await parseJson<{ data: UserCustomAudioGroup[] }>(response);
  return result.data ?? [];
}

export async function fetchUserCustomAudio(
  userId: string | number,
  audioId: string | number
): Promise<UserCustomAudioGroup> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios/${audioId}`,
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const result = await parseJson<{ data: UserCustomAudioGroup }>(response);
  return result.data;
}

export async function fetchUserLinkableVideos(
  userId: string | number
): Promise<LinkableVideo[]> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios/linkable-videos`,
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  const result = await parseJson<{ data: LinkableVideo[] }>(response);
  return result.data ?? [];
}

export async function syncUserCustomAudioVideos(
  userId: string | number,
  audioId: string | number,
  videoIds: number[]
): Promise<UserCustomAudioGroup> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios/${audioId}/videos`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ videoIds }),
    }
  );
  const result = await parseJson<{ data: UserCustomAudioGroup }>(response);
  return result.data;
}

export async function presignUserCustomAudio(
  userId: string | number,
  fileName: string,
  contentType: string
): Promise<PresignUserCustomAudioResponse> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios/presign`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, contentType }),
    }
  );
  const result = await parseJson<{ data: PresignUserCustomAudioResponse }>(
    response
  );
  return result.data;
}

export async function createUserCustomAudios(
  userId: string | number,
  payload: CreateUserCustomAudiosPayload
): Promise<UserCustomAudioGroup> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, videoIds: payload.videoIds ?? [] }),
    }
  );
  const result = await parseJson<{ data: UserCustomAudioGroup }>(response);
  return result.data;
}

export async function deleteUserCustomAudio(
  userId: string | number,
  audioId: number
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/services/admin/users/${userId}/audios/${audioId}`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );
  await parseJson(response);
}

export async function uploadToPresignedUrl(
  putUrl: string,
  file: File,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', putUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = event => {
      if (!event.lengthComputable || !onProgress) return;
      const percent =
        event.total > 0
          ? Math.min(100, Math.round((event.loaded / event.total) * 100))
          : 0;
      onProgress({ loaded: event.loaded, total: event.total, percent });
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.({ loaded: file.size, total: file.size, percent: 100 });
        resolve();
        return;
      }
      reject(
        new Error(`Upload S3 fallito: HTTP ${xhr.status} ${xhr.statusText}`)
      );
    };

    xhr.onerror = () => {
      reject(new Error('Upload S3 fallito: errore di rete'));
    };

    xhr.onabort = () => {
      reject(new Error('Upload S3 annullato'));
    };

    xhr.send(file);
  });
}
